import React, { useState, useCallback } from 'react';
import './SVGSlide.css';

const SVGSlide = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const totalSlides = 15; // 14 revizr_overview SVGs + 1 video slide

  // Minimum swipe distance (in px)
  const minSwipeDistance = 50;

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'F11' || event.key === 'f') {
      // F11 or 'f' key to toggle fullscreen
      event.preventDefault();
      toggleFullscreen();
    } else if (event.key === 'Escape' && isFullscreen) {
      // Escape to exit fullscreen
      exitFullscreen();
    } else if ((event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === ' ') && currentSlide < totalSlides - 1) {
      event.preventDefault(); // Prevent page scroll on spacebar
      setCurrentSlide(currentSlide + 1);
    } else if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  }, [currentSlide, isFullscreen]);

  const nextSlide = () => {
    if (currentSlide < totalSlides - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const getSlideUrl = () => {
    if (currentSlide === 0) {
      return '/revizr_overview.svg';
    } else if (currentSlide === 1) {
      return `/revizr_overview (1).svg`;
    } else if (currentSlide === 2) {
      return null; // Video slide at position 3
    } else if (currentSlide >= 3) {
      // After video slide, continue with revizr_overview (2).svg onwards
      return `/revizr_overview (${currentSlide - 1}).svg`;
    }
  };

  // Fullscreen functions
  const toggleFullscreen = async () => {
    try {
      if (!document.fullscreenElement) {
        await document.documentElement.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Fullscreen not supported or blocked');
    }
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Error exiting fullscreen');
    }
  };

  // Auto-play video when reaching slide 3 (index 2)
  React.useEffect(() => {
    if (currentSlide === 2) {
      setVideoPlaying(true);
    } else {
      setVideoPlaying(false);
    }
  }, [currentSlide]);

  // Listen for fullscreen changes
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Touch handlers for swipe navigation
  const onTouchStart = (e) => {
    // Only handle single touch (not pinch gestures)
    if (e.targetTouches.length !== 1) {
      setTouchStart(null);
      return;
    }
    
    setTouchEnd(null); // Reset touchEnd to avoid false swipe detection
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchMove = (e) => {
    // Only handle single touch (not pinch gestures)
    if (e.targetTouches.length !== 1) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY
    });
  };

  const onTouchEnd = (e) => {
    // Only handle if it was a single touch gesture
    if (!touchStart || !touchEnd || e.changedTouches.length !== 1) {
      setTouchStart(null);
      setTouchEnd(null);
      return;
    }
    
    const distanceX = touchStart.x - touchEnd.x;
    const distanceY = touchStart.y - touchEnd.y;
    
    // Determine if horizontal or vertical swipe is dominant
    const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
    
    if (isHorizontalSwipe) {
      // Horizontal swipes
      const isLeftSwipe = distanceX > minSwipeDistance;
      const isRightSwipe = distanceX < -minSwipeDistance;

      if (isLeftSwipe && currentSlide < totalSlides - 1) {
        setCurrentSlide(currentSlide + 1);
      }
      if (isRightSwipe && currentSlide > 0) {
        setCurrentSlide(currentSlide - 1);
      }
    } else {
      // Vertical swipes
      const isUpSwipe = distanceY > minSwipeDistance;
      const isDownSwipe = distanceY < -minSwipeDistance;

      if (isUpSwipe && currentSlide < totalSlides - 1) {
        // Swipe up - go to next slide
        setCurrentSlide(currentSlide + 1);
      }
      if (isDownSwipe && currentSlide > 0) {
        // Swipe down - go to previous slide
        setCurrentSlide(currentSlide - 1);
      }
    }
    
    // Reset touch states
    setTouchStart(null);
    setTouchEnd(null);
  };

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className="slide" 
         style={{
           position: 'relative',
           width: '100%',
           height: '100%',
           display: 'flex',
           flexDirection: 'column',
           justifyContent: 'center',
           alignItems: 'center',
           padding: 0,
           margin: 0,
           overflow: 'hidden'
         }}
         tabIndex="0"
         onTouchStart={onTouchStart}
         onTouchMove={onTouchMove}
         onTouchEnd={onTouchEnd}>
      
      {/* SVG or Video Display - Full Card */}
      {currentSlide === 2 ? (
        <div style={{
          width: '100%',
          height: '100%',
          position: 'relative',
          background: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <iframe
            width="100%"
            height="100%"
            src={`https://www.youtube.com/embed/taX2yPEoFmQ?autoplay=${videoPlaying ? 1 : 0}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{
              border: 'none'
            }}
          />
        </div>
      ) : (
        <div className="gradient-animation" style={{
          width: '100%',
          height: '100%',
          position: 'relative'
        }}>
          <img
            src={getSlideUrl()}
            alt={`Slide ${currentSlide + 1}`}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              position: 'relative',
              zIndex: 1
            }}
          />
        </div>
      )}

      {/* Navigation Controls */}
      <div style={{
        position: 'absolute',
        bottom: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '15px',
        background: 'rgba(0, 0, 0, 0.7)',
        padding: '10px 20px',
        borderRadius: '25px',
        fontSize: '14px',
        color: '#fff'
      }}>
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          style={{
            background: 'none',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: currentSlide === 0 ? 'rgba(255, 255, 255, 0.3)' : '#fff',
            borderRadius: '20px',
            padding: '8px 16px',
            cursor: currentSlide === 0 ? 'not-allowed' : 'pointer',
            fontSize: '12px'
          }}
        >
          ← Previous
        </button>
        
        <span style={{ fontWeight: 'bold' }}>
          {currentSlide + 1} / {totalSlides}
        </span>
        
        <button
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          style={{
            background: 'none',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: currentSlide === totalSlides - 1 ? 'rgba(255, 255, 255, 0.3)' : '#fff',
            borderRadius: '20px',
            padding: '8px 16px',
            cursor: currentSlide === totalSlides - 1 ? 'not-allowed' : 'pointer',
            fontSize: '12px'
          }}
        >
          Next →
        </button>
        
        <button
          onClick={toggleFullscreen}
          style={{
            background: 'none',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            color: '#fff',
            borderRadius: '20px',
            padding: '8px 12px',
            cursor: 'pointer',
            fontSize: '12px',
            display: 'flex',
            alignItems: 'center',
            gap: '5px'
          }}
          title={isFullscreen ? 'Exit Fullscreen (Esc)' : 'Enter Fullscreen (F11)'}
        >
          {isFullscreen ? (
            <span>⤓</span>
          ) : (
            <span>⤢</span>
          )}
        </button>
      </div>

      {/* Slide Numbers - Dots */}
      <div style={{
        position: 'absolute',
        bottom: '70px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '8px'
      }}>
        {Array.from({ length: totalSlides }, (_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            style={{
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              border: 'none',
              background: currentSlide === index ? '#4169e1' : 'rgba(255, 255, 255, 0.3)',
              cursor: 'pointer',
              transition: 'background-color 0.3s ease'
            }}
          />
        ))}
      </div>
    </div>
  );
};

export default SVGSlide;